import { ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import { Icon, ProfileButton, TransientNavigate, TransientNavLink } from 'src/components';
import { useTheme } from 'src/hooks';
import { texts } from 'src/texts';
import { DashboardPage } from './dashboard/DashboardPage';
import { DeploymentPage } from './deployment/DeploymentPage';
import { DeploymentsPage } from './deployments/DeploymentsPage';
import { ServicesPage } from './services/ServicesPage';
import { ThemePage } from './theme/ThemePage';
import { UserGroupsPage } from './user-groups/UserGroupsPage';
import { UsersPage } from './users/UsersPage';
import { WorkersPage } from './workers/WorkersPage';

// The left margin compensates the transparent border that nav-menu-bordered adds to the items.
const MenuTitle = ({ children }: { children: ReactNode }) => {
  return <div className="mt-6 ml-[5px] px-4 text-xs font-semibold tracking-wide text-gray-600 uppercase">{children}</div>;
};

export const AdminPage = () => {
  const { theme } = useTheme();

  return (
    <div className="flex h-screen flex-col">
      <div className="navbar bg-primary">
        <div className="flex-1">
          <TransientNavLink to="/" className="btn btn-link text-primary-content text-xl no-underline!">
            {theme.name}
          </TransientNavLink>
        </div>
      </div>
      <div className="sidebar-admin flex min-h-0 grow">
        <div className="shadow-xxl flex w-56 shrink-0 flex-col justify-between bg-white">
          <div>
            <ul className="nav-menu nav-menu-bordered mt-4 gap-1">
              <li>
                <TransientNavLink className="flex items-center gap-4" to="/admin/dashboard">
                  <Icon icon="pie-chart" size={16} strokeWidth={1.75} /> {texts.common.dashboard}
                </TransientNavLink>
              </li>
            </ul>

            <MenuTitle>{texts.common.management}</MenuTitle>

            <ul className="nav-menu nav-menu-bordered mt-2 gap-1">
              <li>
                <TransientNavLink className="flex items-center gap-4" to="/admin/services">
                  <Icon icon="file-text" size={16} strokeWidth={1.75} /> {texts.services.headline}
                </TransientNavLink>
              </li>
              <li>
                <TransientNavLink className="flex items-center gap-4" to="/admin/deployments">
                  <Icon icon="server" size={16} strokeWidth={1.75} /> {texts.deployments.headline}
                </TransientNavLink>
              </li>
            </ul>

            <MenuTitle>{texts.common.administration}</MenuTitle>

            <ul className="nav-menu nav-menu-bordered mt-2 gap-1">
              <li>
                <TransientNavLink className="flex items-center gap-4" to="/admin/theme">
                  <Icon icon="droplet" size={16} strokeWidth={1.75} /> {texts.theme.headline}
                </TransientNavLink>
              </li>
              <li>
                <TransientNavLink className="flex items-center gap-4" to="/admin/users">
                  <Icon icon="user" size={16} strokeWidth={1.75} /> {texts.users.headline}
                </TransientNavLink>
              </li>
              <li>
                <TransientNavLink className="flex items-center gap-3" to="/admin/user-groups">
                  <Icon icon="users" size={16} strokeWidth={1.75} /> {texts.userGroups.headline}
                </TransientNavLink>
              </li>
              <li>
                <TransientNavLink className="flex items-center gap-4" to="/admin/workers">
                  <Icon icon="monitor" size={16} strokeWidth={1.75} /> {texts.workers.headline}
                </TransientNavLink>
              </li>
            </ul>
          </div>

          <div className="p-2">
            <ProfileButton menuPlacement="top-start" />
          </div>
        </div>

        <div className="flex min-w-0 grow flex-col items-stretch bg-slate-50">
          <Routes>
            <Route path="/dashboard" element={<DashboardPage />} />

            <Route path="/services/*" element={<ServicesPage />} />

            <Route path="/deployments" element={<DeploymentsPage />} />

            <Route path="/deployments/:deploymentId" element={<DeploymentPage />} />

            <Route path="/theme" element={<ThemePage />} />

            <Route path="/users" element={<UsersPage />} />

            <Route path="/user-groups" element={<UserGroupsPage />} />

            <Route path="/workers" element={<WorkersPage />} />

            <Route path="*" element={<TransientNavigate to="/admin/dashboard" />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};
